import api from './api';

export interface TechWatchSourceConfig {
  query?: string;           // arXiv / OpenAlex search query
  categories?: string[];   // arXiv category filters, e.g. ["cs.AI"]
  max_items?: number;
  issns?: string[];         // OpenAlex journal ISSN filters
}

export interface TechWatchSource {
  id?: string;
  type: 'rss' | 'arxiv' | 'openalex';
  url?: string;             // rss only
  label?: string;
  config?: TechWatchSourceConfig;
}

export interface TechWatch {
  id: string;
  sub_project_id: string;
  client_id: string;
  sources: TechWatchSource[];
  kb_id: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface TechWatchItem {
  id: string;
  watch_id: string;
  source_id: string;
  source_type: 'rss' | 'arxiv';
  source_label: string;
  item_title: string;
  item_date?: string;
  item_guid?: string;
  item_description: string;
  item_url?: string;
  authors?: string;
  abstract?: string;
  audio_url?: string;
  duration_seconds?: number;
  detected_at: string;
  summary?: string;
  relevance_score?: number;
  technical_field?: string;
  digest_title?: string;
  relevance_note?: string;
  user_selected: boolean;
  full_text_status: 'not_downloaded' | 'downloading' | 'downloaded' | 'failed';
  deep_summary?: string;
  problem_relationship?: string;
  stt_status: 'none' | 'requested' | 'transcribing' | 'done' | 'failed';
  document_id?: string;
}

// Backward-compat alias
export type TechWatchEpisode = TechWatchItem;

export interface WatchListItem {
  id: string;
  sub_project_id: string;
  sub_project_name: string;
  project_id: string;
  project_name: string;
  sources: TechWatchSource[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
  latest_digest_at?: string;
  latest_digest_status?: 'pending_review' | 'reviewed' | 'dismissed';
  digest_snippet?: string;
}

export interface TechWatchDigest {
  id: string;
  watch_id: string;
  generated_at: string;
  digest_text: string;
  overall_summary?: string;
  episode_ids: string[];
  status: 'pending_review' | 'reviewed' | 'dismissed';
  user_reviewed_at?: string;
}

export interface EpisodeSummary {
  episode_index: number;
  episode_id?: string;
  title?: string;
  summary: string;
  relevance_score: number;
}

export interface WatchStatus {
  exists: boolean;
  watch_id?: string;
  is_active?: boolean;
  has_pending_digest?: boolean;
  new_episode_count?: number;
}

// CRUD
export const listAllWatches = () =>
  api.get<WatchListItem[]>('/tech-watches/');

export const getWatch = (subProjectId: string) =>
  api.get<TechWatch>(`/tech-watches/${subProjectId}`);

export const createWatch = (subProjectId: string, sources: TechWatchSource[]) =>
  api.post<TechWatch>('/tech-watches/', { sub_project_id: subProjectId, sources });

export const updateWatch = (watchId: string, data: { sources?: TechWatchSource[]; is_active?: boolean }) =>
  api.put<TechWatch>(`/tech-watches/${watchId}`, data);

export const deleteWatch = (watchId: string) =>
  api.delete(`/tech-watches/${watchId}`);

// Status
export const getWatchStatus = (subProjectId: string) =>
  api.get<WatchStatus>(`/tech-watches/status/${subProjectId}`);

// Items (episodes)
export const listEpisodes = (watchId: string) =>
  api.get<TechWatchItem[]>(`/tech-watches/${watchId}/episodes`);

export const getSttStatus = (watchId: string, episodeId: string) =>
  api.get(`/tech-watches/${watchId}/episodes/${episodeId}/stt-status`);

// Item selection (Pass-2)
export const selectItems = (watchId: string, itemIds: string[]) =>
  api.patch(`/tech-watches/${watchId}/items/select`, { item_ids: itemIds });

// Digest
export const getDigest = (watchId: string) =>
  api.get<TechWatchDigest>(`/tech-watches/${watchId}/digest`);

export const generateDigest = (watchId: string) =>
  api.post(`/tech-watches/${watchId}/digest/generate`);

export const reviewDigest = (watchId: string, digestId: string, status: 'reviewed' | 'dismissed') =>
  api.post(`/tech-watches/${watchId}/digest/${digestId}/review`, { status });

// STT
export const requestStt = (watchId: string, episodeIds: string[]) =>
  api.post(`/tech-watches/${watchId}/episodes/stt`, { episode_ids: episodeIds });

// HTML Report
export const getHtmlReport = (watchId: string) =>
  api.get<string>(`/tech-watches/${watchId}/report`, { responseType: 'text' });

// Login hook
export const checkLogin = () =>
  api.post('/tech-watches/check-login');
